import { useMemo, useState } from "react";
import { Modal, Table, Spin, Typography, Button, Alert, Switch, Tooltip, ConfigProvider, Upload, Tag } from "antd";
import { FileTextOutlined, InboxOutlined } from "@ant-design/icons";
import { TrainingAPI } from "@api/TrainingAPI";
import { PermissionedButton, usePermissionCheck } from "@components/PermissionedComponents";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import "@styles/styles-InjuryTable.css";

// A view-only link: users open the sheet without a login, then copy or download it. An empty value hides the link.
const BULK_INJURY_TEMPLATE_URL =
	"https://docs.google.com/spreadsheets/d/1Xv3Hqw3SnOqngnib75PKgwOKQyufD9lQ6jbLWiq0mqs/edit?usp=sharing";

// Response key -> column label.
const FIELD_LABELS = {
	row: "Row",
	player_name: "Player Name",
	injury_type: "Injury Type",
	injury_location: "Injury Location",
	injury_start_date: "Injury Start Date",
	recovery_start_date: "Recovery Start Date",
	injury_end_date: "Injury End Date",
	injury_status: "Status",
	is_soft_tissue: "Soft Tissue",
	is_surgery: "Surgery",
	is_referral: "Referral",
	is_image: "Imaging",
	is_season_ending: "Season Ending",
	activity: "Activity",
	games_lost: "Games Lost",
};

const STATUS_LABELS = { injured: "Injured", recovering: "Recovering", fit: "Fit" };

const renderValue = (value) =>
	value === null || value === undefined || value === "" ? "—" : String(value);

const renderStatus = (value) => STATUS_LABELS[value] || renderValue(value);

// The old value is gray, so the new value stands out.
const renderChanges = (changes) => {
	const entries = Object.entries(changes || {});
	if (entries.length === 0) return "—";
	return entries.map(([field, change]) => {
		const show = field === "injury_status" ? renderStatus : renderValue;
		return (
			<div key={field}>
				<Typography.Text type="secondary">
					{FIELD_LABELS[field] || field}: {show(change?.old)} →
				</Typography.Text>{" "}
				{show(change?.new)}
			</div>
		);
	});
};

// A section title with a count tag. The errors and the preview use the same header.
const SectionTitle = ({ title, tag, color }) => (
	<div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0 8px" }}>
		<Typography.Title level={5} style={{ margin: 0 }}>
			{title}
		</Typography.Title>
		<Tag color={color}>{tag}</Tag>
	</div>
);

const SUMMARY_TEXT_STYLE = { fontSize: 12, margin: "8px 0 0" };

// The row number column is narrow and gray. The CSS class sets the color.
const ROW_COLUMN = { width: 56, className: "bulk-injury-row-cell" };

const makeColumns = (fields) =>
	fields.map((field) => ({
		title: FIELD_LABELS[field] || field,
		dataIndex: field,
		key: field,
		render: field === "injury_status" ? renderStatus : renderValue,
		...(field === "row" ? ROW_COLUMN : {}),
	}));

// A wide table keeps the row number and the player name in view when it scrolls across.
const fixLeft = (columns) =>
	columns.map((column) => (["row", "player_name"].includes(column.key) ? { ...column, fixed: "left" } : column));

const INSERT_COLUMNS = fixLeft(makeColumns([
	"row",
	"player_name",
	"injury_type",
	"injury_location",
	"injury_start_date",
	"recovery_start_date",
	"injury_end_date",
	"injury_status",
	"is_soft_tissue",
	"is_surgery",
	"is_referral",
	"is_image",
	"is_season_ending",
	"activity",
	"games_lost",
]));
const UPDATE_COLUMNS = [
	...makeColumns(["row", "player_name", "injury_type", "injury_location", "injury_start_date"]),
	{ title: "Changes", dataIndex: "changes", key: "changes", render: renderChanges },
];
// The backend skips only the rows of retired players. This names up to
// 10 of their row numbers, for example "rows 4, 9 and 12 belong".
const describeSkippedRows = (skipped) => {
	const numbers = skipped.map((item) => item.row);
	const shown = numbers.slice(0, 10);
	const extra = numbers.length - shown.length;
	const list =
		extra > 0
			? `${shown.join(", ")} and ${extra} more`
			: shown.length > 1
				? `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`
				: `${shown[0]}`;
	const rowWord = numbers.length === 1 ? "row" : "rows";
	const owner = numbers.length === 1 ? "belongs to a retired player" : "belong to retired players";
	return `${numbers.length} ${rowWord} skipped: ${rowWord} ${list} ${owner}.`;
};

const TABLE_PAGINATION = {
	defaultPageSize: 10,
	showSizeChanger: true,
	pageSizeOptions: ["10", "25", "50", "100"],
	hideOnSinglePage: true,
};

const ERROR_PAGE_DEFAULT = { current: 1, pageSize: TABLE_PAGINATION.defaultPageSize };

// Smaller rows for every table in the dialog.
const COMPACT_TABLE_THEME = { components: { Table: { cellPaddingBlockSM: 2, cellFontSizeSM: 12 } } };

const ERROR_CELL_STYLE = { background: "#ffd6d6", padding: 0 };
const WARNING_CELL_STYLE = { background: "#fff1b8", padding: 0 };

const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

const describeError = (error) =>
	error?.response?.status === 403 ? "You do not have permission to do this." : GENERIC_ERROR_MESSAGE;

const addMessage = (map, key, message) => map.set(key, [...(map.get(key) || []), message]);

const cellKey = (row, column) => `${row}\u0000${column}`;

const renderMessages = (messages) => messages.map((message, index) => <div key={index}>{message}</div>);

// A cell with messages gets a tooltip. The tooltip opens on hover and on keyboard focus.
// The inner div fills the cell, also when the cell is blank.
const renderMessageCell = (text, messages) =>
	messages ? (
		<Tooltip trigger={["hover", "focus"]} title={renderMessages(messages)}>
			<div tabIndex={0} style={{ padding: "2px 8px", minHeight: 23 }}>
				{text}
			</div>
		</Tooltip>
	) : (
		text
	);

// Marks the cells that have warnings. getMessages returns the messages of one cell.
const withWarnings = (columns, getMessages) =>
	columns.map((column) => ({
		...column,
		render: (value, record) => renderMessageCell(column.render(value, record), getMessages(record, column)),
		onCell: (record) => ({ style: getMessages(record, column) ? WARNING_CELL_STYLE : undefined }),
	}));

// Reads a validate or save response body.
// A file error body has no columns and no rows.
// A bad inserts or updates field returns neither result.
// The caller then shows a generic error.
const readBulkResponse = (data) => {
	if (Array.isArray(data?.errors)) {
		return {
			errors: data.errors,
			columns: Array.isArray(data.columns) ? data.columns : null,
			rows: Array.isArray(data.rows) ? data.rows : null,
		};
	}
	if (Array.isArray(data?.inserts) && Array.isArray(data?.updates)) return { preview: data };
	return {};
};

const trainingAPI = new TrainingAPI();

// The button and the dialog for a bulk injury CSV file. The backend checks
// and saves the file. This component does not read the file.
export const InjuryBulkUpload = ({ onSaved }) => {
	const { permissionDisabled: editDisabled } = usePermissionCheck("EditPlayerInjuries");
	const permissionCheck = editDisabled ? "EditPlayerInjuries" : "AddPlayerInjuries";

	const [open, setOpen] = useState(false);
	const [file, setFile] = useState(null);
	const [preview, setPreview] = useState(null);
	const [errorResult, setErrorResult] = useState(null);
	const [onlyErrorRows, setOnlyErrorRows] = useState(true);
	const [errorPage, setErrorPage] = useState(ERROR_PAGE_DEFAULT);
	const [validating, setValidating] = useState(false);
	const [saving, setSaving] = useState(false);

	const busy = validating || saving;

	// The lookups use the row number and the header text of the file.
	const errorView = useMemo(() => {
		if (!errorResult) return null;
		const cellMessages = new Map();
		const rowMessages = new Map();
		const errorRows = new Set();
		const fileMessages = [];
		errorResult.errors.forEach(({ row, column, message }) => {
			if (row === null || row === undefined) {
				fileMessages.push(message);
				return;
			}
			errorRows.add(row);
			if (column === null || column === undefined) addMessage(rowMessages, row, message);
			else addMessage(cellMessages, cellKey(row, column), message);
		});
		const hasTable = !!errorResult.rows && !!errorResult.columns;
		const tableRows = hasTable ? errorResult.rows : [];
		return {
			cellMessages,
			rowMessages,
			hasTable,
			tableRows,
			errorTableRows: tableRows.filter((tableRow) => errorRows.has(tableRow.row)),
			// Without a table, the alert shows the message of every error.
			alertMessages: hasTable ? fileMessages : errorResult.errors.map((error) => error.message),
		};
	}, [errorResult]);

	const errorTableColumns = useMemo(() => {
		if (!errorView?.hasTable) return [];
		const { cellMessages, rowMessages } = errorView;
		return [
			{
				title: FIELD_LABELS.row,
				dataIndex: "row",
				key: "row",
				...ROW_COLUMN,
				fixed: "left",
				render: (row) => renderMessageCell(row, rowMessages.get(row)),
				onCell: ({ row }) => ({ style: rowMessages.has(row) ? ERROR_CELL_STYLE : undefined }),
			},
			...errorResult.columns.map((name, index) => ({
				title: name,
				dataIndex: ["cells", index],
				key: `${index}`,
				fixed: name === "Player Name" ? "left" : undefined,
				render: (text, { row }) => renderMessageCell(text, cellMessages.get(cellKey(row, name))),
				onCell: ({ row }) => ({ style: cellMessages.has(cellKey(row, name)) ? ERROR_CELL_STYLE : undefined }),
			})),
		];
	}, [errorView, errorResult]);

	// A warning on an update row marks the Changes cell, because the dates show there.
	const warningColumns = useMemo(() => {
		const cellMessages = new Map();
		const rowMessages = new Map();
		(preview?.warnings || []).forEach(({ row, column, message }) => {
			addMessage(cellMessages, cellKey(row, column), message);
			addMessage(rowMessages, row, message);
		});
		return {
			inserts: withWarnings(INSERT_COLUMNS, ({ row }, { title }) => cellMessages.get(cellKey(row, title))),
			updates: withWarnings(UPDATE_COLUMNS, ({ row }, { key }) => (key === "changes" ? rowMessages.get(row) : undefined)),
		};
	}, [preview]);
	const warningCount = preview?.warnings?.length || 0;

	const resetState = () => {
		setFile(null);
		setPreview(null);
		setErrorResult(null);
		setOnlyErrorRows(true);
		setErrorPage(ERROR_PAGE_DEFAULT);
	};

	const handleClose = () => {
		setOpen(false);
		resetState();
	};

	// Upload calls this for a picked or a dropped file. The return value
	// false stops the Upload component from sending the file itself.
	const handleFile = (picked) => {
		resetState();
		setFile(picked);
		setValidating(true);
		trainingAPI
			.validateBulkInjuries(picked)
			.then((data) => {
				const result = readBulkResponse(data);
				if (result.errors) {
					setErrorResult(result);
					return;
				}
				if (result.preview) {
					setPreview(result.preview);
					return;
				}
				message.error(GENERIC_ERROR_MESSAGE);
			})
			.catch((error) => message.error(describeError(error)))
			.finally(() => setValidating(false));
		return false;
	};

	const canConfirm = !!preview && (preview.inserts.length > 0 || preview.updates.length > 0);

	const handleConfirm = () => {
		setSaving(true);
		trainingAPI
			.saveBulkInjuries(file)
			.then((data) => {
				const result = readBulkResponse(data);
				if (result.errors) {
					setErrorResult(result);
					setPreview(null);
					return;
				}
				if (!result.preview) {
					message.error(GENERIC_ERROR_MESSAGE);
					return;
				}
				message.success(`Saved ${result.preview.inserts.length} new and ${result.preview.updates.length} updated injuries.`);
				onSaved?.();
				handleClose();
			})
			.catch((error) => message.error(describeError(error)))
			.finally(() => setSaving(false));
	};

	return (
		<>
			<PermissionedButton permissionCheck={permissionCheck} onClick={() => setOpen(true)}>
				Bulk Upload Injuries
			</PermissionedButton>

			<ConfigProvider theme={COMPACT_TABLE_THEME}>
				<Modal
					rootClassName="bulk-injury-dialog"
					title="Bulk Upload Injuries"
					open={open}
					onCancel={handleClose}
					closable={!busy}
					maskClosable={!busy}
					keyboard={!busy}
					destroyOnHidden
					footer={null}
					width={1000}
				>
					<Typography.Paragraph type="secondary">
						Make a copy of the{" "}
						{BULK_INJURY_TEMPLATE_URL ? (
							<a href={BULK_INJURY_TEMPLATE_URL} target="_blank" rel="noopener noreferrer">
								template sheet
							</a>
						) : (
							"template sheet"
						)}
						, fill in one injury on each row, download the copy as a CSV file, and upload it below.
					</Typography.Paragraph>

					<Upload.Dragger
						accept=".csv,text/csv"
						multiple={false}
						showUploadList={false}
						beforeUpload={handleFile}
						disabled={busy}
						style={{ marginBottom: 16 }}
					>
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
							{validating ? (
								<Spin size="small" />
							) : file ? (
								<FileTextOutlined style={{ fontSize: 20 }} />
							) : (
								<InboxOutlined style={{ fontSize: 20 }} />
							)}
							<Typography.Text>{file ? file.name : "Click or drag a CSV file here"}</Typography.Text>
							{file && !busy && <Typography.Text type="secondary">(click or drag to change)</Typography.Text>}
						</div>
					</Upload.Dragger>

					{errorView?.alertMessages.length > 0 && (
						<Alert type="error" showIcon message={renderMessages(errorView.alertMessages)} style={{ marginBottom: 16 }} />
					)}

					{errorView?.hasTable && (
						<>
							<SectionTitle
								title="Errors"
								color="error"
								tag={`${errorView.errorTableRows.length} of ${errorView.tableRows.length} ${
									errorView.tableRows.length === 1 ? "row" : "rows"
								}`}
							/>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 8,
									marginBottom: 8,
								}}
							>
								<Typography.Text type="secondary" style={{ fontSize: 12 }}>
									Hover over a red cell to see the error.
								</Typography.Text>
								<Switch
									checked={onlyErrorRows}
									checkedChildren="Errors"
									unCheckedChildren="All rows"
									aria-label="Show only the rows with errors"
									onChange={(checked) => {
										setOnlyErrorRows(checked);
										setErrorPage((page) => ({ ...page, current: 1 }));
									}}
								/>
							</div>
							<Table
								rowKey="row"
								columns={errorTableColumns}
								dataSource={onlyErrorRows ? errorView.errorTableRows : errorView.tableRows}
								pagination={{
									...TABLE_PAGINATION,
									current: errorPage.current,
									pageSize: errorPage.pageSize,
									onChange: (current, pageSize) => setErrorPage({ current, pageSize }),
								}}
								size="small"
								bordered
								scroll={{ x: "max-content" }}
							/>
						</>
					)}

					{preview && (
						<>
							{warningCount > 0 && (
								<div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
									<Tag color="warning">{`${warningCount} ${warningCount === 1 ? "warning" : "warnings"}`}</Tag>
									<Typography.Text type="secondary" style={{ fontSize: 12 }}>
										Hover over a yellow cell to see the warning. You can still confirm.
									</Typography.Text>
								</div>
							)}
							<SectionTitle title="New Injuries" color="success" tag={preview.inserts.length} />
							<Table
								rowKey="row"
								columns={warningColumns.inserts}
								dataSource={preview.inserts}
								pagination={TABLE_PAGINATION}
								size="small"
								bordered
								scroll={{ x: "max-content" }}
								locale={{ emptyText: "No new injuries." }}
							/>

							<SectionTitle title="Updated Injuries" color="processing" tag={preview.updates.length} />
							<Table
								rowKey="row"
								columns={warningColumns.updates}
								dataSource={preview.updates}
								pagination={TABLE_PAGINATION}
								size="small"
								bordered
								scroll={{ x: "max-content" }}
								locale={{ emptyText: "No updated injuries." }}
							/>

							<Typography.Paragraph type="secondary" style={SUMMARY_TEXT_STYLE}>
								{preview.unchanged_count === 1
									? "1 injury needs no change."
									: `${preview.unchanged_count || 0} injuries need no change.`}
							</Typography.Paragraph>

							{preview.skipped?.length > 0 && (
								<Typography.Paragraph type="secondary" style={SUMMARY_TEXT_STYLE}>
									{describeSkippedRows(preview.skipped)}
								</Typography.Paragraph>
							)}
						</>
					)}

					<div
						style={{
							marginTop: 16,
							paddingTop: 12,
							borderTop: "1px solid #f0f0f0",
							display: "flex",
							alignItems: "center",
							gap: 8,
						}}
					>
						<Typography.Text type="secondary" style={{ flex: 1 }}>
							{errorResult && "Fix the errors and upload the file again."}
							{canConfirm &&
								`Confirm saves ${preview.inserts.length} new and ${preview.updates.length} updated ${
									preview.inserts.length + preview.updates.length === 1 ? "injury" : "injuries"
								}.`}
						</Typography.Text>
						<Button onClick={handleClose} disabled={busy}>
							Cancel
						</Button>
						<PermissionedButton
							type="primary"
							permissionCheck={permissionCheck}
							disabled={!canConfirm}
							loading={saving}
							onClick={handleConfirm}
						>
							Confirm
						</PermissionedButton>
					</div>
				</Modal>
			</ConfigProvider>
		</>
	);
};
